import { Configs } from 'constants/configs'
import { IExerciseRecords } from 'hooks/useExerciseRecords'
import { FC, ReactNode, useMemo } from 'react'
import { LuSchool, LuTimer, LuUser } from 'react-icons/lu'
import { BarChart } from 'components/BarChart'
import { ChartBox, Section, SectionHeading } from 'components/ui/Section'
import { ChartSkeleton, EmptyState, Skeleton } from 'components/ui/States'
import { formatClock, formatFixed, formatInt, formatMinSec, groupByClass, groupByGrade, groupByStudent, isValidStudent, ISummary, safeDivide } from 'utils/stats'
import { RecordCardView } from './recordCardView'

interface IParticipationSection {
    data: IExerciseRecords[]
    summary: ISummary
    range: string
    loading: boolean
}

const RankBadge: FC<{ rank: number }> = ({ rank }) => (
    <span className={`grid h-7 w-7 shrink-0 place-items-center rounded-full text-xs font-semibold tabular-nums ${rank <= 3 ? 'bg-primary text-white' : 'bg-canvas-deep text-ink-muted'}`}>{rank}</span>
)

const Th: FC<{ children: ReactNode, right?: boolean, className?: string }> = ({ children, right, className = '' }) => (
    <th scope='col' className={`whitespace-nowrap px-4 py-3 ${right ? 'text-right' : ''} ${className}`}>{children}</th>
)

const TableSkeleton: FC = () => <div className='space-y-3 p-4 sm:p-6'>
    {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className='h-4 w-full' />)}
</div>

/** Section I: who participates. Valid student keys only. */
export const ParticipationSection: FC<IParticipationSection> = ({ data, summary, range, loading }) => {
    const grades = useMemo(() => groupByGrade(data), [data])
    const classes = useMemo(() => groupByClass(data, 10), [data])
    const allClassesCount = useMemo(() => groupByClass(data, Number.MAX_SAFE_INTEGER).length, [data])
    const students = useMemo(() => groupByStudent(data, 10), [data])
    const studentSeconds = useMemo(
        () => data.reduce((total, r) => (isValidStudent(r) ? total + (r.durationInSecond ?? 0) : total), 0),
        [data]
    )
    const noStudents = !loading && summary.students === 0

    return <div className='space-y-4 lg:space-y-6'>
        <SectionHeading title='參與分析' description='各年級、班級及學生的參與情況（只計算已登記學生）' range={range} />

        <div className='grid grid-cols-1 gap-4 min-[420px]:grid-cols-3 lg:gap-6'>
            <RecordCardView
                title='人均運動時間'
                icon={<LuTimer />}
                value={formatMinSec(safeDivide(studentSeconds, summary.students))}
                description='每位已登記學生的總運動時間'
                loading={loading} />
            <RecordCardView
                title='平均每位學生次數'
                icon={<LuUser />}
                value={formatFixed(safeDivide(summary.studentSessions, summary.students), 1)}
                unit={summary.students ? '次' : undefined}
                loading={loading} />
            <RecordCardView title='參與班級數' icon={<LuSchool />} value={formatInt(allClassesCount)} unit='班' loading={loading} />
        </div>

        {noStudents
            ? <div className='card'><EmptyState title='此期間沒有已登記學生的紀錄' hint={`另有 ${formatInt(summary.unregisteredSessions)} 次未登記學生`} /></div>
            : <>
                <div className='grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-6'>
                    <Section title='各年級參與' subtitle={`次數（左軸）及學生人數（右軸）· ${range}`}>
                        {loading
                            ? <ChartSkeleton />
                            : <ChartBox>
                                <BarChart
                                    categories={grades.map((g) => `${g.grade}年級`)}
                                    series={[
                                        { name: '次數', data: grades.map((g) => g.sessions) },
                                        { name: '學生人數', data: grades.map((g) => g.students) },
                                    ]}
                                    units={['次', '人']}
                                    yTitle='次數'
                                    y2Title='學生人數' />
                            </ChartBox>}
                    </Section>

                    <Section title='班級排行' subtitle={`按次數排序 · 前10名 · ${range}`} bodyClassName='!p-0'>
                        {loading
                            ? <TableSkeleton />
                            : <div className='overflow-x-auto'>
                                <table className='w-full min-w-[480px] text-left text-sm'>
                                    <thead className='bg-canvas-deep text-xs font-semibold text-ink-muted'>
                                        <tr>
                                            <Th className='sm:pl-6'>排名</Th>
                                            <Th>班級</Th>
                                            <Th right>次數</Th>
                                            <Th right>完成次數</Th>
                                            <Th right>分鐘</Th>
                                            <Th right className='sm:pr-6'>學生</Th>
                                        </tr>
                                    </thead>
                                    <tbody className='divide-y divide-line-soft'>
                                        {classes.map((c, i) => <tr key={c.label} className='transition-colors hover:bg-canvas'>
                                            <td className='px-4 py-2.5 sm:pl-6'><RankBadge rank={i + 1} /></td>
                                            <td className='whitespace-nowrap px-4 py-2.5 font-medium text-ink'>{c.label}班</td>
                                            <td className='px-4 py-2.5 text-right font-semibold tabular-nums'>{formatInt(c.sessions)}</td>
                                            <td className='px-4 py-2.5 text-right tabular-nums'>{formatInt(c.reps)}</td>
                                            <td className='px-4 py-2.5 text-right tabular-nums'>{formatInt(c.seconds / 60)}</td>
                                            <td className='px-4 py-2.5 text-right tabular-nums sm:pr-6'>{formatInt(c.students)}</td>
                                        </tr>)}
                                    </tbody>
                                </table>
                            </div>}
                    </Section>
                </div>

                <Section title='最活躍學生' subtitle={`按次數排序（同次數以完成次數排序）· 前10名 · ${range}`} bodyClassName='!p-0'>
                    {loading
                        ? <TableSkeleton />
                        : <div className='overflow-x-auto'>
                            <table className='w-full min-w-[560px] text-left text-sm'>
                                <thead className='bg-canvas-deep text-xs font-semibold text-ink-muted'>
                                    <tr>
                                        <Th className='sm:pl-6'>排名</Th>
                                        <Th>學生</Th>
                                        <Th right>次數</Th>
                                        <Th right>完成次數</Th>
                                        <Th right className='sm:pr-6'>運動時間</Th>
                                    </tr>
                                </thead>
                                <tbody className='divide-y divide-line-soft'>
                                    {students.map((s, i) => <tr key={s.key} className='transition-colors hover:bg-canvas'>
                                        <td className='px-4 py-2.5 sm:pl-6'><RankBadge rank={i + 1} /></td>
                                        <td className='px-4 py-2.5'>
                                            <span className='flex items-center gap-3'>
                                                {s.iconUrl
                                                    ? <img src={`${Configs.baseUrl}${s.iconUrl}`} alt='' className='h-8 w-8 shrink-0 rounded-full bg-canvas-deep object-cover' />
                                                    : <span className='grid h-8 w-8 shrink-0 place-items-center rounded-full bg-canvas-deep text-ink-faint'><LuUser aria-hidden className='h-4 w-4' /></span>}
                                                <span className='whitespace-nowrap font-medium text-ink'>{s.label}</span>
                                            </span>
                                        </td>
                                        <td className='px-4 py-2.5 text-right font-semibold tabular-nums'>{formatInt(s.sessions)} 次</td>
                                        <td className='px-4 py-2.5 text-right tabular-nums'>{formatInt(s.reps)} 下</td>
                                        <td className='whitespace-nowrap px-4 py-2.5 text-right tabular-nums sm:pr-6' title={`${formatInt(s.seconds)} 秒`}>{formatClock(s.seconds)}</td>
                                    </tr>)}
                                </tbody>
                            </table>
                        </div>}
                </Section>
            </>}
    </div>
}
